/** Presentation-only field help; all supplied copy is rendered as plain text. */
export function createFieldHelp({ id, text, buttonLabel }: {
  id: string;
  text: string;
  buttonLabel: string;
}): HTMLElement {
  const help = document.createElement('span');
  help.className = 'field-help';
  const button = document.createElement('button');
  button.className = 'field-help-button';
  button.type = 'button';
  button.setAttribute('aria-label', buttonLabel);
  button.setAttribute('aria-controls', id);
  button.setAttribute('aria-describedby', id);
  button.setAttribute('aria-expanded', 'false');
  button.textContent = 'i';
  const popover = document.createElement('span');
  popover.className = 'field-help-popover';
  popover.id = id;
  popover.setAttribute('role', 'tooltip');
  popover.hidden = true;
  popover.textContent = text;
  help.append(button, popover);
  return help;
}

export function bindFieldHelp(form: HTMLFormElement): () => void {
  type Help = {
    root: HTMLElement;
    button: HTMLButtonElement;
    popover: HTMLElement;
    hovered: boolean;
    focused: boolean;
    pinned: boolean;
    suppressed: boolean;
  };
  const entries = new Map<HTMLElement, Help>();
  let current: Help | undefined;
  const ownerDocument = form.ownerDocument;

  function resolve(target: EventTarget | null): Help | undefined {
    if (!(target instanceof Element)) return;
    const root = target.closest<HTMLElement>('.field-help');
    if (!root || !form.contains(root)) return;
    const existing = entries.get(root);
    if (existing) return existing;
    const button = root.querySelector<HTMLButtonElement>('.field-help-button');
    const popover = root.querySelector<HTMLElement>('.field-help-popover');
    if (!button || !popover) return;
    const help = { root, button, popover, hovered: false, focused: false, pinned: false, suppressed: false };
    entries.set(root, help);
    return help;
  }

  function close(help: Help, suppress = false): void {
    help.popover.hidden = true;
    help.button.setAttribute('aria-expanded', 'false');
    help.pinned = false;
    help.suppressed = suppress;
    if (current === help) current = undefined;
  }

  function open(help: Help): void {
    if (help.suppressed) return;
    if (current && current !== help) close(current);
    current = help;
    help.popover.hidden = false;
    help.button.setAttribute('aria-expanded', 'true');
  }

  function leave(help: Help): void {
    if (!help.hovered && !help.focused) {
      help.suppressed = false;
      if (!help.pinned) close(help);
    }
  }

  function pointerOver(event: PointerEvent): void {
    if (event.pointerType === 'touch') return;
    const help = resolve(event.target);
    if (!help || (event.relatedTarget instanceof Node && help.root.contains(event.relatedTarget))) return;
    help.hovered = true;
    open(help);
  }

  function pointerOut(event: PointerEvent): void {
    if (event.pointerType === 'touch') return;
    const help = resolve(event.target);
    if (!help || (event.relatedTarget instanceof Node && help.root.contains(event.relatedTarget))) return;
    help.hovered = false;
    leave(help);
  }

  function focusIn(event: FocusEvent): void {
    const help = resolve(event.target);
    if (!help) return;
    help.focused = true;
    open(help);
  }

  function focusOut(event: FocusEvent): void {
    const help = resolve(event.target);
    if (!help || (event.relatedTarget instanceof Node && help.root.contains(event.relatedTarget))) return;
    help.focused = false;
    leave(help);
  }

  function click(event: MouseEvent): void {
    const help = resolve(event.target);
    if (!help || !(event.target instanceof Node) || !help.button.contains(event.target)) return;
    event.preventDefault();
    if (help.pinned) close(help, true);
    else {
      // Focus/hover may already have opened this help before the first tap.
      help.suppressed = false;
      open(help);
      help.pinned = true;
    }
  }

  function outside(event: Event): void {
    if (current && event.target instanceof Node && !current.root.contains(event.target)) close(current, true);
  }

  function keyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !current) return;
    const help = current;
    const focusWasInside = ownerDocument.activeElement !== help.button
      && help.root.contains(ownerDocument.activeElement);
    close(help, true);
    event.preventDefault();
    if (focusWasInside) help.button.focus();
  }

  form.addEventListener('pointerover', pointerOver);
  form.addEventListener('pointerout', pointerOut);
  form.addEventListener('focusin', focusIn);
  form.addEventListener('focusout', focusOut);
  form.addEventListener('click', click);
  ownerDocument.addEventListener('pointerdown', outside);
  ownerDocument.addEventListener('click', outside);
  ownerDocument.addEventListener('keydown', keyDown);
  return () => {
    form.removeEventListener('pointerover', pointerOver);
    form.removeEventListener('pointerout', pointerOut);
    form.removeEventListener('focusin', focusIn);
    form.removeEventListener('focusout', focusOut);
    form.removeEventListener('click', click);
    ownerDocument.removeEventListener('pointerdown', outside);
    ownerDocument.removeEventListener('click', outside);
    ownerDocument.removeEventListener('keydown', keyDown);
    for (const help of entries.values()) close(help);
    entries.clear();
  };
}
